import { Link } from "react-router-dom";
import { Rocket, Target, Code2, BookOpen, Eye, Users, Award, Building2 } from "lucide-react";
import { Reveal, Stagger, Item } from "@/components/site/Reveal";
import StatCounter from "@/components/site/StatCounter";
import { VALUES, TIMELINE, STATS } from "@/lib/content";

const ICONS = { Rocket, Target, Code2, BookOpen, Eye, Users };

export default function About() {
  return (
    <div data-testid="about-page" className="pt-32 pb-20">
      {/* Hero */}
      <section className="relative mx-auto max-w-7xl px-6 md:px-12 mesh-bg pb-16">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">
            <Link to="/" className="hover:text-foreground">Home</Link> · About
          </p>
          <h1 className="mt-5 font-display text-5xl md:text-7xl tracking-tight leading-[0.95] max-w-4xl">
            Building tomorrow's <span className="text-gradient">solutions</span>, today.
          </h1>
          <p className="mt-7 text-xl text-zinc-400 max-w-2xl">
            A passionate team of engineers, designers, and strategists — partnering with founders
            and operators to ship what the next chapter of their business depends on.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-14 aspect-[16/8] rounded-3xl overflow-hidden border border-white/[0.08]">
            <img src="https://images.pexels.com/photos/6804612/pexels-photo-6804612.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Fullstack Endeavours studio" className="w-full h-full object-cover" />
          </div>
        </Reveal>
      </section>

      {/* Mission */}
      <section className="mx-auto max-w-5xl px-6 md:px-12 mt-24">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">[ Mission ]</p>
          <h2 className="mt-6 font-display text-4xl md:text-6xl tracking-tight leading-[1.05]">
            We exist to <span className="text-gradient">bridge</span> the gap between business
            vision and technical reality.
          </h2>
          <p className="mt-8 text-xl text-zinc-400 leading-relaxed">
            Through innovative software, deep design thinking, and a relentless focus on outcomes,
            we empower companies to scale, compete, and lead in the digital age.
          </p>
        </Reveal>
      </section>

      {/* Values */}
      <section data-testid="values-section" className="mx-auto max-w-7xl px-6 md:px-12 mt-32">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">[ Values ]</p>
          <h2 className="mt-5 font-display text-4xl md:text-5xl tracking-tight">
            The principles that guide our work.
          </h2>
        </Reveal>
        <Stagger className="mt-12 grid md:grid-cols-3 gap-5">
          {VALUES.map((v) => {
            const Icon = ICONS[v.icon] || Rocket;
            return (
              <Item key={v.title}>
                <div className="rounded-2xl glass border-white/[0.08] p-8 h-full hover:bg-white/[0.04] transition">
                  <div className="h-11 w-11 grid place-items-center rounded-xl bg-white/5 border border-white/10">
                    <Icon className="h-5 w-5 text-nexus-cyan" />
                  </div>
                  <h3 className="mt-6 font-display text-2xl tracking-tight">{v.title}</h3>
                  <p className="mt-3 text-zinc-400 leading-relaxed">{v.body}</p>
                </div>
              </Item>
            );
          })}
        </Stagger>
      </section>

      {/* Timeline */}
      <section data-testid="timeline-section" className="mx-auto max-w-7xl px-6 md:px-12 mt-32">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">[ Journey ]</p>
          <h2 className="mt-5 font-display text-4xl md:text-5xl tracking-tight">
            Eight years, one obsession.
          </h2>
        </Reveal>

        <div className="mt-16 relative">
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent" />
          <Stagger className="space-y-12">
            {TIMELINE.map((t, i) => (
              <Item key={t.year}>
                <div className={`relative grid md:grid-cols-2 gap-6 items-center ${i % 2 ? "md:[direction:rtl]" : ""}`}>
                  <div className={`pl-12 md:pl-0 ${i % 2 ? "md:[direction:ltr] md:text-right md:pr-16" : "md:pl-16"}`}>
                    <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan">{t.year}</span>
                    <h3 className="mt-2 font-display text-3xl tracking-tight">{t.title}</h3>
                    <p className="mt-2 text-zinc-400 leading-relaxed">{t.body}</p>
                  </div>
                  <div className="absolute left-2.5 md:left-1/2 md:-translate-x-1/2 top-1.5 h-3 w-3 rounded-full bg-nexus-cyan glow-cyan" />
                  <div />
                </div>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Stats */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 mt-32">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">[ By the numbers ]</p>
          <h2 className="mt-5 font-display text-4xl md:text-5xl tracking-tight">A track record we're proud of.</h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-5">
          {STATS.map((s) => (
            <div key={s.label} className="rounded-2xl glass border-white/[0.08] p-8 text-center md:text-left">
              <div className="font-display text-5xl md:text-6xl tracking-tighter text-gradient">
                <StatCounter value={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Partners */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 mt-32">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">[ Partners & certifications ]</p>
          <h2 className="mt-5 font-display text-4xl md:text-5xl tracking-tight">Recognized work.</h2>
        </Reveal>

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl glass border-white/[0.08] p-8">
            <div className="flex items-center gap-3 mb-6">
              <Award className="h-4 w-4 text-nexus-cyan" />
              <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">Partnerships</span>
            </div>
            <ul className="grid grid-cols-2 gap-y-3 text-sm">
              {["AWS Advanced Tier", "Microsoft Gold", "Google Cloud Partner", "Shopify Plus", "Stripe Verified", "Vercel Partner"].map((p) => (
                <li key={p} className="text-zinc-300">{p}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl glass border-white/[0.08] p-8">
            <div className="flex items-center gap-3 mb-6">
              <Building2 className="h-4 w-4 text-nexus-cyan" />
              <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">Certifications</span>
            </div>
            <ul className="grid grid-cols-2 gap-y-3 text-sm">
              {["ISO 27001", "SOC 2 Type II", "HIPAA Compliant", "PCI DSS", "GDPR", "ADA / WCAG 2.1"].map((p) => (
                <li key={p} className="text-zinc-300">{p}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
